// Página de Login com autenticação Supabase
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { login } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { Loading } from '../../components/common/Loading';

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading, refreshSession } = useAuth();
  // Guardar referência para usar no handleSubmit
  const [hasRedirected, setHasRedirected] = useState(false);
  const [loginSuccessful, setLoginSuccessful] = useState(false);

  // Redirecionar se já estiver autenticado (apenas uma vez)
  // Este useEffect cuida tanto de redirecionamento após login quanto de redirecionamento se já estiver autenticado
  useEffect(() => {
    console.log('[Login] Estado de autenticação:', { isAuthenticated, loading, hasRedirected });

    // Redirecionar quando estiver autenticado e não estiver carregando
    if (!loading && isAuthenticated && !hasRedirected) {
      console.log('[Login] ✅ Usuário autenticado detectado, redirecionando...');
      const from = (location.state as any)?.from?.pathname || '/app';
      setHasRedirected(true);
      setIsLoading(false); // Desativar loading quando for redirecionar

      // Redirecionar imediatamente (sem setTimeout para evitar atrasos)
      console.log('[Login] Executando redirecionamento para:', from);
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, location, hasRedirected]);

  // Redirecionamento de emergência: se após login bem-sucedido e autenticado, forçar redirecionamento após 1 segundo
  useEffect(() => {
    if (loginSuccessful && isAuthenticated && !loading && !hasRedirected) {
      const timeout = setTimeout(() => {
        console.log('[Login] Redirecionamento de emergência - forçando...');
        const from = (location.state as any)?.from?.pathname || '/app';
        setHasRedirected(true);
        setIsLoading(false);
        navigate(from, { replace: true });
      }, 1000);

      return () => clearTimeout(timeout);
    }
  }, [loginSuccessful, isAuthenticated, loading, hasRedirected, navigate, location]);

  // Timeout de segurança: se após login bem-sucedido não redirecionou em 3 segundos, desativar loading e forçar redirecionamento
  useEffect(() => {
    if (loginSuccessful && !hasRedirected) {
      const timeout = setTimeout(() => {
        if (isAuthenticated && !hasRedirected) {
          console.warn('[Login] Timeout: forçando redirecionamento após 3 segundos');
          const from = (location.state as any)?.from?.pathname || '/app';
          setHasRedirected(true);
          setIsLoading(false);
          navigate(from, { replace: true });
        } else {
          console.warn('[Login] Timeout: redirecionamento não ocorreu em 3 segundos, desativando loading');
          setIsLoading(false);
        }
      }, 3000);

      return () => clearTimeout(timeout);
    }
  }, [loginSuccessful, hasRedirected, isAuthenticated, navigate, location]);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setLoginSuccessful(false);

    try {
      console.log('[Login] Iniciando login...');

      // Fazer login via Supabase
      const result = await login(formData.email, formData.password);
      console.log('[Login] Login bem-sucedido:', result);

      if (!result || !result.session) {
        throw new Error('Sessão não foi criada corretamente');
      }

      // Marcar login como bem-sucedido
      // O evento SIGNED_IN será disparado automaticamente pelo Supabase
      // O useEffect acima vai detectar quando isAuthenticated ficar true e redirecionar
      setLoginSuccessful(true);

      // O evento SIGNED_IN será disparado automaticamente pelo Supabase
      // O listener do AuthContext já atualiza o estado automaticamente
      // Não precisamos chamar refreshSession aqui, pois ele pode causar loading=true
      // Aguardar um pouco para o listener processar o evento
      await new Promise(resolve => setTimeout(resolve, 400));

      // O redirecionamento será feito pelo useEffect quando isAuthenticated ficar true e loading ficar false
      // O listener do Supabase garante que loading seja false após atualizar o estado

    } catch (err: any) {
      console.error('[Login] Erro no login:', err);
      setError(err.message || 'Erro ao fazer login. Verifique suas credenciais.');
      setIsLoading(false);
      setLoginSuccessful(false);
    }
  };

  // Mostrar loading enquanto verifica autenticação
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  // Se já estiver autenticado, não mostrar o formulário
  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-700 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">NFe</span>
            </div>
            <span className="text-2xl font-bold text-gray-900">proNotas</span>
          </div>
          <p className="text-green-600 font-semibold">Simples, prático e rápido.</p>
        </div>

        {/* Card de Login */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Bem-vindo de volta</h1>
          <p className="text-gray-600 mb-6">Entre com suas credenciais para acessar a plataforma</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                setError(null);
              }}
              placeholder="seu@email.com"
              required
              disabled={isLoading}
            />

            <div className="relative">
              <Input
                label="Senha"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => {
                  setFormData({ ...formData, password: e.target.value });
                  setError(null);
                }}
                placeholder="••••••••"
                required
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2">
                <input type="checkbox" className="rounded border-gray-300 text-green-600 focus:ring-green-500" />
                <span className="text-sm text-gray-600">Lembrar-me</span>
              </label>
              <a href="#" className="text-sm text-green-600 hover:text-green-700">
                Esqueceu a senha?
              </a>
            </div>

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              size="lg"
              isLoading={isLoading}
              disabled={isLoading || !formData.email || !formData.password}
            >
              {isLoading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Não tem uma conta?{' '}
              <a href="/" className="text-green-600 hover:text-green-700 font-medium">
                Comece grátis
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
